import locator from '@/service-locator'
import MerchantProfileApi from '@/api/merchant/profile'
import MERCHANT_TYPE from '@/constants/merchant-type'
import {
  MERCHANT_CALLBACK_TIER_CONFIG,
  MERCHANT_TIER_TO_CALLBACK_CODE,
} from '@/constants/merchant-tier'
import { getABTProvider } from '@/plugins/abt/statsig'
import { getLanguageFromLocale } from '@/utils/i18n'

const state = {
  data: {},
  fetched: false,
  fetching: false,
  error: null,
}

const getters = {
  profile: (state) => {
    return state.data || {}
  },
  inStoreDirectIntegrationEnabled: (state) => {
    return state.data?.inStoreDirectIntegrationEnabled
  },
  merchantTier(_, getters) {
    const tierInfo = getters.profile?.tier

    return tierInfo?.tier || ''
  },
  merchantTypes(_, getters) {
    return getters.profile?.merchantTypes || []
  },
  isMerchantOnline(_, getters) {
    return getters.merchantTypes.includes(MERCHANT_TYPE.online)
  },
  isMerchantInStore(_, getters) {
    return getters.merchantTypes.includes(MERCHANT_TYPE.in_store)
  },
  merchantCountryCode(_, getters) {
    return getters.profile.country || ''
  },
  countryCodeLowerCase(_, getters) {
    return getters.profile?.country?.toLowerCase() || ''
  },
  isT3Merchant: (state) => {
    const tier = state.data?.tier?.tier || ''
    return ['Tier 3', 'T3', 'Tier 3 - Unverified Pilot'].includes(tier)
  },
  shouldAllowRequestCallback: (_, getters) => {
    const abtProvider = getABTProvider()
    const config =
      abtProvider.getConfigurations(MERCHANT_CALLBACK_TIER_CONFIG) || {}
    const enabledTiers = config['merchant-tier'] || []

    if (!enabledTiers.length) {
      return false
    }

    const merchantTierCode =
      MERCHANT_TIER_TO_CALLBACK_CODE[getters.merchantTier] || ''

    return enabledTiers.includes(merchantTierCode)
  },
}

const mutations = {
  SET_STATUS: (state, { key, value }) => {
    state[key] = value
  },
  SET_MERCHANT_DATA: (state, data) => {
    state.data = data
  },
  SET_MERCHANT_ERROR: (state, error) => {
    state.error = error
  },
}

const actions = {
  async getProfile({ commit }) {
    try {
      // checking for essential data from /profile api
      if (state.data && state.data.merchantId && state.data.merchantName) {
        return state.data
      }

      commit('SET_STATUS', { key: 'fetching', value: true })

      const { data } = await MerchantProfileApi.getProfile()

      // Also fetch merchant user profile so we can enrich chatbot context
      let userProfile = null
      try {
        userProfile = await locator.store.dispatch(
          'userProfile/getMerchantUserProfile'
        )
      } catch (error) {
        console.warn(
          'Failed to fetch merchant user profile for chat meta',
          error
        )
      }

      const metaFields = {
        merchant_authenticated_status: true,
        merchant_name: data.merchantName || '',
        merchant_id: data.merchantId || '',
        merchant_tier: data.tier || '',
        merchant_code: data.merchantCode || '',
        merchant_platform: data.platform || '',
        merchant_email: userProfile?.email || '',
        user_id: userProfile?.userId || '',
        first_name: userProfile?.firstName || '',
      }

      /**
       * Edge case : If merchant is logged in from a different country,
       * we need to update the Statsig user context with the correct country
       * from merchant profile
       */

      if (data.merchantId) {
        const abtProvider = getABTProvider()
        await abtProvider
          .updateUser({
            userID: userProfile?.userId || '',
            country: data.country || '',
            customIDs: {
              merchant_id: data.merchantId,
            },
          })
          .catch(() => {
            console.warn('Failed to update Statsig user context')
          })

        const detectedLang = getLanguageFromLocale(
          locator.store.getters.currentLocale
        )

        // Initialize Salesforce authenticated chatbots
        console.warn('[SF Chat] initChatBot authenticated')
        await locator.sfChatbot.setChatMetaFields(metaFields)
        locator.sfChatbot.initChatBot({
          language: detectedLang,
          metaFields,
        })

        if (
          state.data.merchantId &&
          state.data.merchantId !== data.merchantId
        ) {
          window.location.reload()
        }
      }

      commit('SET_STATUS', { key: 'fetched', value: true })
      commit('SET_STATUS', { key: 'fetching', value: false })
      commit('SET_MERCHANT_DATA', data)
    } catch (error) {
      commit('SET_MERCHANT_ERROR', error)
    }
  },
  resetProfile({ commit }) {
    commit('SET_MERCHANT_DATA', {})
  },
}

export default { namespaced: true, state, getters, mutations, actions }
